import { H6 } from "@dgs/core";
import { SortableContext, useSortable, verticalListSortingStrategy } from "@dnd-kit/sortable";
import React, { useMemo } from "react";
import styled from "styled-components";
import { EmptySortableItem, SortableItem } from "./SortableItem";
import { ISortableItem } from "./SortableListItem";

const List = styled.div`
	display: grid;
	grid-auto-flow: row;
	grid-auto-columns: 1fr;
	grid-auto-rows: max-content;
	gap: ${({ theme }) => theme.spacing(1)};
	overflow: hidden;
`;

interface Props<T extends ISortableItem> {
	heading?: string;
	needle?: string;
	columnId: string;
	getLabel: (item: T) => string;
	renderLabel?: (item: T) => React.ReactNode;
	items: T[];
}

interface DroppableContainerProps {
	children: React.ReactNode;
	id: string;
}

function DroppableContainer({ children, id }: DroppableContainerProps): React.ReactNode {
	const { setNodeRef } = useSortable({
		id,
	});

	return <div ref={setNodeRef}>{children}</div>;
}

export function SortableList<T extends ISortableItem>({
	renderLabel,
	getLabel,
	needle = "",
	heading,
	columnId,
	items,
}: Props<T>) {
	const filteredItems = useMemo<T[]>(() => {
		return items.filter((item) => getLabel(item).toLowerCase().includes(needle.toLowerCase()));
	}, [getLabel, items, needle]);

	return (
		<DroppableContainer id={`${heading}-container`}>
			<List>
				{heading && <H6>{heading}</H6>}
				<SortableContext id={columnId} items={items} strategy={verticalListSortingStrategy}>
					{filteredItems.map((item) => (
						<SortableItem key={item.id} item={item} getLabel={getLabel} renderLabel={renderLabel} />
					))}
					{filteredItems.length === 0 && <EmptySortableItem />}
				</SortableContext>
			</List>
		</DroppableContainer>
	);
}
